import type { ObjectId } from 'mongodb';

import type { Meta } from '../Entities';

import { PayoutEvent } from './Events';
import type { ProjectTransactionDocument } from './Transactions';

export enum ProjectClawbackStatus {
  /** The clawback could happen, it's waiting for the trigger date. */
  pending = 'pending',
  /** The clawback trigger date was passed, and it was processed (but a transaction was not necessarily created). */
  completed = 'completed',
  /** This potential clawback no longer applies. The account was cancelled, etc. */
  cancelled = 'cancelled',
  /** An updated clawback has replaced this one, with a new trigger date, etc. */
  replaced = 'replaced',
}

export enum ProjectClawbackRuleNames {
  /** NTP payout event occurred, but install has not occurred within the threshold */
  noticeToProceed = 'noticeToProceed',
  /** Install payout was made, but activation has not occurred within the threshold */
  install = 'install',
  /** We have paid a materials vendor (estimate or invoice), but EPC has not submitted install milestone within the threshold (typically 14 days) */
  partnerInstallSubmittal = 'partnerInstallSubmittal',
  /** We have paid a materials vendor (estimate or invoice) and the EPC has reached Install Submitted, but has not reached install approved within the threshold (typically 30 days) */
  partnerInstallApproval = 'partnerInstallApproval',
  /** Claw back everything we have paid to the EPC */
  cancellation = 'cancellation',
  /** Claw back payments made under the previous payment plan */
  paymentPlanChange = 'paymentPlanChange',
}

export const clawbackRuleNameOptions: { value: ProjectClawbackRuleNames; label: string }[] = [
  { value: ProjectClawbackRuleNames.noticeToProceed, label: 'Notice to Proceed' },
  { value: ProjectClawbackRuleNames.install, label: 'Install' },
  { value: ProjectClawbackRuleNames.partnerInstallSubmittal, label: 'Materials - Partner Install Submittal' },
  { value: ProjectClawbackRuleNames.partnerInstallApproval, label: 'Materials - Partner Install Approval' },
  { value: ProjectClawbackRuleNames.cancellation, label: 'Cancellation' },
  // { value: ProjectClawbackRuleNames.paymentPlanChange, label: 'Payment Plan Change' }, per Justin Kiser, we do not want to show this option in the UI filter.
];

/** @deprecated Use ProjectClawbackRuleNames instead */
export enum ClawbackCategory {
  cancellation = 'cancellation',
  ntp = 'ntp',
  install = 'install',
  partnerInstallNotComplete = 'partnerInstallNotComplete',
  paymentPlanChange = 'paymentPlanChange',
}

export const clawbackCategoryOptions: { value: ProjectClawbackRuleNames; label: string }[] = [
  { value: ProjectClawbackRuleNames.noticeToProceed, label: 'NTP' },
  { value: ProjectClawbackRuleNames.cancellation, label: 'Cancellation' },
  { value: ProjectClawbackRuleNames.install, label: 'Install' },
  { value: ProjectClawbackRuleNames.partnerInstallSubmittal, label: 'Partner Install Not Completed' },
];

export type ClawbackSummary = {
  date: Date;
  transactions?: Array<Pick<ProjectTransactionDocument, 'id' | 'amount' | 'batchId'>>;
  rule?: ProjectClawbackRuleNames;
};

export const materialsClawbackRules: ProjectClawbackRuleNames[] = [
  ProjectClawbackRuleNames.partnerInstallSubmittal,
  ProjectClawbackRuleNames.partnerInstallApproval,
];

export type ProjectClawbackRule = {
  name: ProjectClawbackRuleNames;
  /** The source payment event that causes this rule to be run. Some rules are triggered via external means (eg. CED ship notifications). */
  sourceEvent?: PayoutEvent;
  sourceEvents?: PayoutEvent[];
  /** The target event each clawback rule 'timer' is measured against.
   * Eg. The NTP rule causes a clawback transaction if the project doesn't reach Install Approved before the trigger date.
   */
  targetEvent?: PayoutEvent;
  /** If the rule criteria is met, these are the payout events that would have clawback transactions generated. */
  clawsBackEvents: PayoutEvent[];
};

/** Source events that trigger the next clawback rule */
export const payoutEventRuleTriggers: Record<PayoutEvent, ProjectClawbackRuleNames | null> = {
  [PayoutEvent.noticeToProceed]: ProjectClawbackRuleNames.noticeToProceed,
  // No NTP+, NTP event handles it for the purposes of clawbacks
  [PayoutEvent.noticeToProceedPlus]: null,
  [PayoutEvent.installSubmitted]: ProjectClawbackRuleNames.partnerInstallApproval,
  [PayoutEvent.installApproved]: ProjectClawbackRuleNames.install,
  [PayoutEvent.estimate]: ProjectClawbackRuleNames.partnerInstallSubmittal,
  [PayoutEvent.invoice]: ProjectClawbackRuleNames.partnerInstallSubmittal,
  [PayoutEvent.ptoApproved]: null,
  [PayoutEvent.activationApproved]: null,
  [PayoutEvent.siteSurveyApproved]: null, // Site survey fee is not subject to clawback
  [PayoutEvent.siteSurvey]: null, // Site survey credit is not subject to clawback
  [PayoutEvent.standardHoldback]: null,
  [PayoutEvent.domesticContentHoldback]: null,
  [PayoutEvent.other]: null,
  [PayoutEvent.warranty]: null,
  [PayoutEvent.propertyType]: null,
  [PayoutEvent.equipment]: null,
  [PayoutEvent.shipping]: null,
  [PayoutEvent.loopFieldApproved]: null, // Geothermal loop field — no clawback at launch
};

export const clawbackRuleDefinitions: ProjectClawbackRule[] = [
  {
    name: ProjectClawbackRuleNames.noticeToProceed,
    sourceEvent: PayoutEvent.noticeToProceed,
    targetEvent: PayoutEvent.installApproved,
    clawsBackEvents: [PayoutEvent.noticeToProceed, PayoutEvent.noticeToProceedPlus],
  },
  {
    name: ProjectClawbackRuleNames.install,
    sourceEvents: [PayoutEvent.installSubmitted, PayoutEvent.installApproved],
    targetEvent: PayoutEvent.activationApproved,
    clawsBackEvents: [
      PayoutEvent.noticeToProceed,
      PayoutEvent.noticeToProceedPlus,
      PayoutEvent.installSubmitted,
      PayoutEvent.installApproved,
      PayoutEvent.ptoApproved,
      PayoutEvent.estimate,
      PayoutEvent.invoice,
    ],
  },
  {
    name: ProjectClawbackRuleNames.partnerInstallSubmittal,
    sourceEvents: [PayoutEvent.invoice, PayoutEvent.estimate],
    targetEvent: PayoutEvent.installSubmitted,
    clawsBackEvents: [PayoutEvent.estimate, PayoutEvent.invoice],
  },
  {
    name: ProjectClawbackRuleNames.partnerInstallApproval,
    sourceEvent: PayoutEvent.installSubmitted,
    targetEvent: PayoutEvent.installApproved,
    clawsBackEvents: [PayoutEvent.estimate, PayoutEvent.invoice],
  },
  {
    name: ProjectClawbackRuleNames.cancellation,
    clawsBackEvents: Object.values(PayoutEvent), // claw back everything
  },
];

export interface ProjectClawbackHistory {
  date: Date;
  user: { id: string; name: string };
  status: ProjectClawbackStatus;
  replacedBy?: string; // or something more generic?
  transactionId?: ObjectId; // transaction that was created as a result of processing this clawback
}

export interface ProjectClawbackDocument {
  id?: string;
  projectId: ObjectId;
  /** LR accountId */
  projectExternalId: string;
  /** The org settings that was used to determine the number of days */
  organizationSettingsId: ObjectId;
  sourceRuleName: ProjectClawbackRuleNames;
  sourceEvent?: PayoutEvent;
  sourceEventDate: Date;
  targetEvent?: PayoutEvent;
  triggerDate: Date;
  /** Days after sourceEventDate that will trigger a clawback transaction. @see triggerDate. */
  triggerDays: number; // days after source event to trigger clawback
  clawsBackEvents: PayoutEvent[];
  status: ProjectClawbackStatus;
  /** A transaction that may have been created as a result of processing this clawback. */
  /** @deprecated */
  transactionId?: ObjectId;
  transactionIds?: ObjectId[]; // multiple transactions can be created for a clawback
  /** The amount that would be clawed back if this clawback is processed, as calculated at the time of creation. */
  potentialAmount: number;
  meta?: Meta;
  history?: Array<ProjectClawbackHistory>;
}
